class UniversalPopup {
  constructor() {
    this.activePopups = new Set();
    this.focusTrapElements = new Map();
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.relocatePopupsToBody();
        this.setupEventListeners();
      });
    } else {
      this.relocatePopupsToBody();
      this.setupEventListeners();
    }
  }

  relocatePopupsToBody() {
    document.querySelectorAll('.am-popup').forEach((popup) => {
      if (popup.parentElement !== document.body) {
        document.body.appendChild(popup);
      }
    });
  }

  setupEventListeners() {
    document.addEventListener('click', (e) => {
      const openTrigger = e.target.closest('[data-popup-open]');
      if (openTrigger) {
        e.preventDefault();
        const popupId = openTrigger.getAttribute('data-popup-open');
        this.open(popupId);
      }

      const closeTrigger = e.target.closest('[data-popup-close]');
      if (closeTrigger) {
        e.preventDefault();
        const popupId = closeTrigger.getAttribute('data-popup-close');
        this.close(popupId);
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activePopups.size > 0) {
        const lastPopup = Array.from(this.activePopups).pop();
        this.close(lastPopup);
      }
    });
  }

  activate(popup) {
    popup.classList.remove('am-popup--closing');
    popup.style.display = 'flex';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      popup.classList.add('am-popup--active');
      return;
    }
    void popup.offsetWidth;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (popup.classList.contains('am-popup--closing')) return;
        popup.classList.add('am-popup--active');
      });
    });
  }

  open(popupId) {
    const popup = document.getElementById(`am-popup-${popupId}`);
    if (!popup) {
      console.warn(`Popup with id "am-popup-${popupId}" not found`);
      return;
    }

    if (this.activePopups.has(popupId)) {
      return;
    }

    if (popup.parentElement !== document.body) {
      document.body.appendChild(popup);
    }

    if (this.activePopups.size === 0) {
      window.themeScrollLock?.lock?.();
    }

    this.activePopups.add(popupId);

    this.setupFocusTrap(popup, popupId);

    popup.dispatchEvent(new CustomEvent('popup:open', {
      bubbles: true,
      detail: { popupId }
    }));

    popup.querySelectorAll('.am-popup__handle').forEach((el) => el.remove());
    this.activate(popup);
    this.focusFirstElement(popup);
  }

  clearPopupOpenState() {
    if (window.themeScrollLock?.isLocked?.()) {
      window.themeScrollLock.unlock();
    }
  }

  close(popupId) {
    const popup = document.getElementById(`am-popup-${popupId}`);
    if (!popup) {
      return;
    }

    if (!popup.classList.contains('am-popup--active') && !this.activePopups.has(popupId)) {
      return;
    }

    popup.classList.add('am-popup--closing');
    popup.classList.remove('am-popup--active');
    this.activePopups.delete(popupId);

    this.removeFocusTrap(popupId);

    popup.dispatchEvent(new CustomEvent('popup:close', {
      bubbles: true,
      detail: { popupId }
    }));

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hideDelay = reduceMotion ? 0 : 480;

    setTimeout(() => {
      if (!this.activePopups.has(popupId)) {
        popup.style.display = 'none';
        popup.classList.remove('am-popup--closing');
      }

      if (this.activePopups.size === 0) {
        this.clearPopupOpenState();
      }
    }, hideDelay);
  }

  closeAll() {
    const popups = Array.from(this.activePopups);
    popups.forEach(popupId => this.close(popupId));
  }

  setupFocusTrap(popup, popupId) {
    const focusableElements = popup.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    popup.addEventListener('keydown', handleTab);
    this.focusTrapElements.set(popupId, handleTab);
  }

  removeFocusTrap(popupId) {
    const popup = document.getElementById(`am-popup-${popupId}`);
    const handler = this.focusTrapElements.get(popupId);
    
    if (popup && handler) {
      popup.removeEventListener('keydown', handler);
      this.focusTrapElements.delete(popupId);
    }
  }

  focusFirstElement(popup) {
    const initialFocusElement = popup.querySelector('[data-popup-initial-focus]');
    const focusableElements = popup.querySelectorAll(
      'input:not([type="hidden"]), select, textarea, button:not(.am-popup__close-btn)'
    );

    const elementToFocus = initialFocusElement || focusableElements[0];

    if (elementToFocus instanceof HTMLElement) {
      setTimeout(() => {
        elementToFocus.focus({ preventScroll: true });
      }, 350);
    }
  }

  isOpen(popupId) {
    return this.activePopups.has(popupId);
  }

  getActivePopups() {
    return Array.from(this.activePopups);
  }
}

const popupManager = new UniversalPopup();

window.openPopup = (popupId) => popupManager.open(popupId);
window.closePopup = (popupId) => popupManager.close(popupId);
window.closeAllPopups = () => popupManager.closeAll();
window.isPopupOpen = (popupId) => popupManager.isOpen(popupId);

if (typeof module !== 'undefined' && module.exports) {
  module.exports = UniversalPopup;
}

document.addEventListener('submit', (e) => {
  const form = e.target;
  const popup = form.closest('.am-popup');
  
  if (popup) {
    const popupId = popup.getAttribute('data-popup-id');
    
    if (form.hasAttribute('data-popup-close-on-submit')) {
      setTimeout(() => {
        window.closePopup(popupId);
      }, 100);
    }
  }
});
